import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { ToggleSwitch } from '@/components/Base/ToggleSwitch/ToggleSwitch'
import { ScenarioAnalysisOverviewChart } from '@/components/ScenarioAnalysisReport/Charts/ScenarioAnalysisOverviewChart'
import { ScenarioAnalysisReportCard } from '@/components/ScenarioAnalysisReport/ScenarioAnalysisReportCard'
import { ScenarioAnalysisReportNavigation } from '@/components/ScenarioAnalysisReport/ScenarioAnalysisReportNavigation'
import { SelectScenarios } from '@/components/ScenarioAnalysisReport/SelectScenarios'
import { ScenarioAnalysisOverviewTable } from '@/components/ScenarioAnalysisReport/Tables/ScenarioAnalysisOverviewTable'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IScenarioAnalysisCardDef,
  IScenarioAnalysisReportOverviewDTO,
} from '@/interfaces/scenarioAnalysisReportComponents.interface'
import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import {
  getColumnDefByReportType,
  getScenarioAnalysisOverViewReport,
} from '@/services/axiomReport.service'
import {
  getAllScenarios,
  getColDefsForScenarioOverview,
  transformScenarioAnalysisData,
} from '@/utils/scenarioAnalysisReportUtils/scenarioAnalysisReportUtils'
import { AgGridReact } from '@ag-grid-community/react'
import { Box, Stack, Typography } from '@mui/material'

import React, { useEffect, useState } from 'react'

export const ScenarioAnalysisDashboardPage: React.FC = () => {
  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const [focusedContent, setFocusedContent] = useState<string>('')
  const [rawReportData, setRawreportData] = useState<
    IScenarioAnalysisReportOverviewDTO[]
  >([])
  const [processedScenarioReport, setProcessedScenarioReport] = useState<any[]>(
    [],
  )
  const [displayBenchmarkData, setDisplayBenchmarkData] = useState(false)
  const [allScenarios, setAllScenarios] = useState<string[]>([])
  const [scenarios, setScenarios] = useState<string[]>([])
  const [scenarioColDef, setScenarioColDef] = useState<any[]>([])
  const [gridReference, setGridReference] = useState<AgGridReact>()
  const [isLoading, setLoading] = useState(false)

  const fcHeight = (document.body.clientHeight * 50) / 100

  const toggleFocusedContent = (sectionName: string) => {
    if (focusedContent === sectionName) {
      setFocusedContent('')
    } else {
      setFocusedContent(sectionName)
    }
  }

  const getReport = async () => {
    setLoading(true)
    try {
      const res = await getScenarioAnalysisOverViewReport(
        selectedReport.reportId,
      )
      setRawreportData(res)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const scenarioList: string[] = getAllScenarios(rawReportData)
    setScenarios(scenarioList)
    setAllScenarios(scenarioList)
  }, [rawReportData])

  useEffect(() => {
    const filteredDataForSelectedScenarios = rawReportData?.filter((item) =>
      scenarios.includes(item.scenario_name),
    )
    const transformedData = transformScenarioAnalysisData(
      filteredDataForSelectedScenarios,
    )
    setProcessedScenarioReport(transformedData)

    const updatedColDef = getColDefsForScenarioOverview(
      scenarios,
      displayBenchmarkData,
    )
    setScenarioColDef(updatedColDef)
  }, [displayBenchmarkData, scenarios])

  const scenarioAnalysisCardDefs: IScenarioAnalysisCardDef[] = [
    {
      title: <Typography variant='h5'>Total Return</Typography>,
      id: 'totalReturnChart',
      defaultGridColumn: '1 / 2',
      defaultGridRow: '1 / 2',
      focusedGridColumn: '1 / 6',
      focusedGridRow: '1 / 5',
      focusAllowed: true,
      loading: isLoading,
      showIcon: false,
      childComponent: (
        <ScenarioAnalysisOverviewChart
          key='totalReturnChart'
          defaultChartHeight='60%'
          focusedChartHeight={fcHeight}
          id='totalReturnChart'
          data={processedScenarioReport}
          dataPoint='total_return_contrib'
          isFocused={focusedContent === 'totalReturnChart'}
          displayBenchmarkData={displayBenchmarkData}
        />
      ),
    },
    {
      title: <Typography variant='h5'>Market Value + Accrued</Typography>,
      id: 'marketValuePlusAccruedChart',
      defaultGridColumn: '2 / 3',
      defaultGridRow: '1 / 2',
      focusedGridColumn: '1 / 6',
      focusedGridRow: '1 / 5',
      focusAllowed: true,
      loading: isLoading,
      showIcon: false,
      childComponent: (
        <ScenarioAnalysisOverviewChart
          key='marketValuePlusAccruedChart'
          defaultChartHeight='60%'
          focusedChartHeight={fcHeight}
          id='marketValuePlusAccruedChart'
          data={processedScenarioReport}
          dataPoint='horizon_proceed'
          isFocused={focusedContent === 'marketValuePlusAccruedChart'}
          displayBenchmarkData={false} // always false because Market Value +  Accrued Chart will never display Benchmark
        />
      ),
    },
    {
      title: <Typography variant='h5'>Yield</Typography>,
      id: 'yieldChart',
      defaultGridColumn: '3 / 4',
      defaultGridRow: '1 / 2',
      focusedGridColumn: '1 / 6',
      focusedGridRow: '1 / 5',
      focusAllowed: true,
      loading: isLoading,
      showIcon: false,
      childComponent: (
        <ScenarioAnalysisOverviewChart
          key='yieldChart'
          defaultChartHeight='60%'
          focusedChartHeight={fcHeight}
          id='yieldChart'
          data={processedScenarioReport}
          dataPoint='horizon_ytm'
          isFocused={focusedContent === 'yieldChart'}
          displayBenchmarkData={false}
        />
      ),
    },
    {
      title: <Typography variant='h5'>Duration</Typography>,
      id: 'durationChart',
      defaultGridColumn: '4 / 5',
      defaultGridRow: '1 / 2',
      focusedGridColumn: '1 / 6',
      focusedGridRow: '1 / 5',
      focusAllowed: true,
      loading: isLoading,
      showIcon: false,
      childComponent: (
        <ScenarioAnalysisOverviewChart
          key='durationChart'
          defaultChartHeight='60%'
          focusedChartHeight={fcHeight}
          id='durationChart'
          data={processedScenarioReport}
          dataPoint='horizon_edur'
          isFocused={focusedContent === 'durationChart'}
          displayBenchmarkData={displayBenchmarkData}
        />
      ),
    },
    {
      title: <Typography variant='h5'>Convexity</Typography>,
      id: 'convexityChart',
      defaultGridColumn: '5 / 6',
      defaultGridRow: '1 / 2',
      focusedGridColumn: '1 / 6',
      focusedGridRow: '1 / 5',
      focusAllowed: true,
      loading: isLoading,
      showIcon: false,
      childComponent: (
        <ScenarioAnalysisOverviewChart
          key='convexityChart'
          defaultChartHeight='60%'
          focusedChartHeight={fcHeight}
          id='convexityChart'
          data={processedScenarioReport}
          dataPoint='horizon_convex'
          isFocused={focusedContent === 'convexityChart'}
          displayBenchmarkData={displayBenchmarkData}
        />
      ),
    },
    {
      title: <Typography variant='h5'>Scenario Details</Typography>,
      id: 'scenariosTable',
      defaultGridColumn: '1 / 6',
      defaultGridRow: '2 / 5',
      focusedGridColumn: '1 / 7',
      focusedGridRow: '1 / 7',
      focusAllowed: false,
      loading: isLoading,
      showIcon: true,
      childComponent: (
        <ScenarioAnalysisOverviewTable
          data={processedScenarioReport}
          columns={scenarioColDef}
          displayBenchmarkData={displayBenchmarkData}
          setGridReference={setGridReference}
        />
      ),
    },
  ]
  const fetchColumnDefByReportType = async () => {
    try {
      const colRes = await getColumnDefByReportType(AxiomReportType.SCEN)
      sessionStorage.setItem(AxiomReportType.SCEN, JSON.stringify(colRes))
    } catch (err) {}
  }

  useEffect(() => {
    getReport()
    const isStoredInSession = sessionStorage.getItem(AxiomReportType.SCEN)
    if (!isStoredInSession) {
      fetchColumnDefByReportType()
    }
  }, [selectedReport])

  return (
    <ReportDashboardLayout
      fitAllInView
      selectedReport={selectedReport}
      navigation={<ScenarioAnalysisReportNavigation />}
      action={
        <Stack
          direction='row'
          justifyContent='center'
          alignItems={'center'}
          gap={8}
        >
          <SelectScenarios
            allScenarios={allScenarios}
            visibleScenarios={scenarios}
            updateScenarios={setScenarios}
            color='secondary'
            size='small'
            badgeColor='primary'
          />
          <ToggleSwitch
            checked={displayBenchmarkData}
            label='Show Benchmark'
            color='success'
            size='medium'
            handleChange={setDisplayBenchmarkData}
          />
        </Stack>
      }
    >
      <Box
        display='grid'
        gridTemplateColumns='repeat(5, 1fr)'
        gridTemplateRows='repeat(4, 1fr)'
        gap={4}
        height='inherit'
      >
        {scenarioAnalysisCardDefs?.map((card) => {
          return (
            <ScenarioAnalysisReportCard
              key={card.id}
              title={card.title}
              cardId={card.id}
              isFocused={focusedContent === card.id}
              toggleFocus={toggleFocusedContent}
              defaultGridColumn={card.defaultGridColumn}
              defaultGridRow={card.defaultGridRow}
              focusedGridColumn={card.focusedGridColumn}
              focusedGridRow={card.focusedGridRow}
              focusAllowed={card.focusAllowed}
              showIcon={card.showIcon}
              gridReference={gridReference}
            >
              {card.loading ? (
                <LoadingCard />
              ) : rawReportData?.length ? (
                <Box
                  display='grid'
                  alignItems='start'
                  justifyContent='stretch'
                  alignSelf='start'
                  height='100%'
                >
                  {card.childComponent}
                </Box>
              ) : (
                <NoDataToDisplayCard />
              )}
            </ScenarioAnalysisReportCard>
          )
        })}
      </Box>
    </ReportDashboardLayout>
  )
}
